import { Box } from '@mui/material';
import { useMemo, useState } from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { LoadingSkeleton, FormTitleBox, CustomButton, InfoDialog } from '../../common/components';
import AuditFilters from './components/AuditFilters';
import AuditTable from './components/AuditTable';
import AuditDetailsDialog from './components/AuditDetailsDialog';
import { useAuditLogs } from './hooks/useAuditLogs';
import { useUsers } from '../../hooks/useUsers';
import { useAuditReferenceData } from './hooks/useAuditReferenceData';
import { fetchAllHistoricalActions, fetchHistoricalActionById } from '../../hooks/useHistoricalActions';
import { exportAuditActionsToExcel, exportAuditActionsToPdf } from './utils/exportAuditReports';

const MAX_EXPORT_AUDITS = 2000;

const AuditLogs = () => {
  const [exportingType, setExportingType] = useState('');
  const [exportLimitDialogOpen, setExportLimitDialogOpen] = useState(false);
  const {
    modules,
    modulesLoading,
    filters,
    page,
    perPage,
    total,
    totalPages,
    actions,
    isLoading,
    isFetching,
    handleFilterChange,
    handleResetFilters,
    handleSearch,
    handlePageChange,
    handlePerPageChange,
    selectedAction,
    handleSelectAction,
    handleCloseAction,
    queryParams,
  } = useAuditLogs();
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { resolveReference } = useAuditReferenceData();
  const formattedTotal = Number(total || 0).toLocaleString('es-AR');

  const fetchActionsWithDetails = async (items = []) => {
    const BATCH_SIZE = 25;
    const detailed = [];

    for (let index = 0; index < items.length; index += BATCH_SIZE) {
      const chunk = items.slice(index, index + BATCH_SIZE);
      const chunkResults = await Promise.all(
        chunk.map(async (action) => {
          try {
            return await fetchHistoricalActionById(action.id);
          } catch (_error) {
            return action;
          }
        })
      );
      detailed.push(...chunkResults);
    }

    return detailed;
  };

  const selectedUserLabel = useMemo(() => {
    if (!filters?.userUuid) return '';
    const selectedUser = users.find((user) => user?.uuid === filters.userUuid);
    if (!selectedUser) return filters.userUuid;
    return selectedUser.names || selectedUser.email || filters.userUuid;
  }, [filters?.userUuid, users]);

  const handleExport = async (type) => {
    if (Number(total || 0) > MAX_EXPORT_AUDITS) {
      setExportLimitDialogOpen(true);
      return;
    }

    setExportingType(type);
    try {
      const allActions = await fetchAllHistoricalActions(queryParams);
      const exportActions = await fetchActionsWithDetails(allActions);

      const exportFilters = {
        ...filters,
        userLabel: selectedUserLabel,
      };

      if (type === 'xlsx') {
        await exportAuditActionsToExcel({
          actions: exportActions,
          filters: exportFilters,
          resolveReference,
          filenamePrefix: 'Auditoria_Filtrada',
        });
      } else {
        exportAuditActionsToPdf({
          actions: exportActions,
          filters: exportFilters,
          resolveReference,
          filenamePrefix: 'Auditoria_Filtrada',
        });
      }
    } catch (error) {
      console.error('Error exporting audit report:', error);
    } finally {
      setExportingType('');
    }
  };

  const handleExportSingleAction = async (actionToExport, type, loadingKey) => {
    if (!actionToExport) return;
    setExportingType(loadingKey);
    try {
      const detailedAction = await fetchHistoricalActionById(actionToExport.id);
      if (type === 'xlsx') {
        await exportAuditActionsToExcel({
          actions: [detailedAction],
          filters: {},
          resolveReference,
          filenamePrefix: `Auditoria_Detalle_${actionToExport.id}`,
        });
      } else {
        exportAuditActionsToPdf({
          actions: [detailedAction],
          filters: {},
          resolveReference,
          filenamePrefix: `Auditoria_Detalle_${actionToExport.id}`,
        });
      }
    } catch (error) {
      console.error('Error exporting audit detail:', error);
    } finally {
      setExportingType('');
    }
  };

  return (
    <Box>
      <FormTitleBox
        title="Auditoría | Acciones históricas"
      >
        <CustomButton
          variant="info"
          buttonIcon={TableChartIcon}
          onClick={() => handleExport('xlsx')}
          isLoading={exportingType === 'xlsx'}
          disabled={isFetching}
          sx={{ px: 2.5 }}
        >
          Descargar XLSX
        </CustomButton>
        <CustomButton
          variant="info"
          buttonIcon={PictureAsPdfIcon}
          onClick={() => handleExport('pdf')}
          isLoading={exportingType === 'pdf'}
          disabled={isFetching}
          sx={{ px: 2.5 }}
        >
          Descargar PDF
        </CustomButton>
      </FormTitleBox>

      <AuditFilters
        modules={modules}
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        onSearch={handleSearch}
        isSearching={isFetching}
        users={users}
        usersLoading={usersLoading}
      />

      <AuditTable
        actions={actions}
        isLoading={isLoading}
        page={page}
        totalPages={totalPages}
        totalCount={total}
        onPageChange={handlePageChange}
        rowsPerPage={perPage}
        onRowsPerPageChange={handlePerPageChange}
        onSelectAction={handleSelectAction}
        onExportAction={(actionToExport, type) =>
          handleExportSingleAction(
            actionToExport,
            type,
            type === 'xlsx' ? `row-xlsx-${actionToExport?.id}` : `row-pdf-${actionToExport?.id}`
          )
        }
      />

      <AuditDetailsDialog
        open={Boolean(selectedAction)}
        onClose={handleCloseAction}
        action={selectedAction}
        resolveReference={resolveReference}
        onExportAction={(actionToExport, type) => {
          handleExportSingleAction(
            actionToExport,
            type,
            type === 'xlsx' ? 'detail-xlsx' : 'detail-pdf'
          );
        }}
        exportLoading={exportingType === 'detail-xlsx' || exportingType === 'detail-pdf'}
      />

      <InfoDialog
        open={exportLimitDialogOpen}
        onClose={() => setExportLimitDialogOpen(false)}
        title="Descarga limitada"
        message={`No se puede descargar más de ${MAX_EXPORT_AUDITS} auditorías en una sola exportación. Registros actuales: ${formattedTotal}. Podés ver la cantidad total al pie de la tabla (ejemplo: Registros: ${formattedTotal}). Por favor acotá los filtros para obtener hasta ${MAX_EXPORT_AUDITS} registros.`}
      />
    </Box>
  );
};

export default AuditLogs;
